import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { Grid, GridColumn } from "@progress/kendo-react-grid";

import { optimumFrictionConstants } from "../../../../../_redux/_constants/optimumFriction.constants";
import { optimumFrictionGetTrainingImportsRequest } from "../../../../../_redux/_actions";

const INITIAL_PAGE = { skip: 0, take: 10 };

// Mirrors the real row height and the default page size, so the table does not jump when the data
// arrives.
const HistorySkeleton = () => (
     <Box sx={{ pt: 1 }}>
          {Array.from({ length: 8 }).map((row, index) => (
               <Skeleton key={index} height={44} />
          ))}
     </Box>
);

// What has been uploaded before. Without it an administrator cannot tell a file that was never
// uploaded from one that was uploaded and granted nothing, because the import leaves only a flag on
// each participant.
export default function OptimumFrictionImportHistoryGrid() {
     const actionDispatch = useDispatch();

     const history = useSelector(
          (state) => state?.optimumFriction?.trainingImports,
     );

     const isLoading = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               optimumFrictionConstants.OPTIMUM_FRICTION_GET_TRAINING_IMPORTS_REQUEST,
          ),
     );

     const [page, setPage] = useState(INITIAL_PAGE);

     useEffect(() => {
          actionDispatch(
               optimumFrictionGetTrainingImportsRequest({
                    page: Math.floor(page.skip / page.take) + 1,
                    pageSize: page.take,
               }),
          );
     }, [actionDispatch, page]);

     const rows = useMemo(() => {
          const imports = history?.imports || [];

          return imports.map((row) => ({
               ...row,
               uploaded: row.uploadedOn
                    ? moment(row.uploadedOn).format("DD/MM/YYYY HH:mm")
                    : "",
          }));
     }, [history]);

     const totalCount = history?.totalCount || 0;

     return (
          <Box sx={{ pt: 4 }}>
               <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                    Previous uploads
               </Typography>

               {isLoading && rows.length === 0 ? (
                    <HistorySkeleton />
               ) : (
                    <Grid
                         data={rows}
                         skip={page.skip}
                         take={page.take}
                         total={totalCount}
                         className="sba-area-data-grid"
                         pageable={{ buttonCount: 4, pageSizes: [10, 25, 50] }}
                         onPageChange={(event) => setPage(event.page)}
                    >
                         <GridColumn
                              field="fileName"
                              title="File"
                              width="320px"
                         />
                         <GridColumn
                              field="uploaded"
                              title="Uploaded"
                              width="180px"
                         />
                         <GridColumn
                              field="rowCount"
                              title="Rows"
                              width="120px"
                         />
                         <GridColumn
                              field="grantedCount"
                              title="Accredited"
                              width="140px"
                         />
                         <GridColumn
                              field="notGrantedCount"
                              title="Not granted"
                              width="150px"
                         />
                    </Grid>
               )}
          </Box>
     );
}
